import React, { useEffect, useRef, useState } from 'react';
import {
  Plus, Trash2, Copy, Banknote, Wallet, CreditCard, Receipt, Tag, Utensils, Sandwich, Coffee, Bed, Bus, Train,
  Car, Navigation, Mountain, Gauge, Droplets, Zap, Moon, Sun, Snowflake, Umbrella, Clock, Ticket, Gift,
  Briefcase, Wrench, HeartPulse, Users, Phone, Star, Ban, ChevronDown,
} from 'lucide-react';

/**
 * Driver allowances on the Acting Fare form: Food, Stay, the one-way return bus fare and
 * any number of custom charges.
 *
 * How it prices (the fare engine does the real work, from what is saved here):
 *   - the customer ticks an option on the booking screen to say "I will arrange this
 *     myself" -> nothing is charged for it;
 *   - an option left unticked is added to the fare as its own line, at booking and again
 *     at the end of the trip using the REAL days, hours and drop point.
 *
 * The preview tables mirror the engine's arithmetic so an admin can see what a 1-, 2-, 3-day
 * trip will cost before saving. They are a preview only: what is charged is always worked out
 * on the server.
 */

// Icons a charge can carry. `name` is what is saved: an Ionicons name from the customer app,
// and ONLY these, because the phone can draw just the icons bundled in it (the fare engine
// passes exactly this list through and the save refuses anything else). `Icon` is the
// closest look-alike for the preview here.
export const ICON_CHOICES = [
  { name: 'cash-outline', label: 'Cash', Icon: Banknote },
  { name: 'wallet-outline', label: 'Wallet', Icon: Wallet },
  { name: 'card-outline', label: 'Card', Icon: CreditCard },
  { name: 'receipt-outline', label: 'Receipt', Icon: Receipt },
  { name: 'pricetag-outline', label: 'Tag', Icon: Tag },
  { name: 'restaurant-outline', label: 'Meal', Icon: Utensils },
  { name: 'fast-food-outline', label: 'Fast food', Icon: Sandwich },
  { name: 'cafe-outline', label: 'Tea / coffee', Icon: Coffee },
  { name: 'bed-outline', label: 'Stay', Icon: Bed },
  { name: 'bus-outline', label: 'Bus', Icon: Bus },
  { name: 'train-outline', label: 'Train', Icon: Train },
  { name: 'car-outline', label: 'Car', Icon: Car },
  { name: 'navigate-outline', label: 'Route', Icon: Navigation },
  { name: 'trail-sign-outline', label: 'Hill / sign', Icon: Mountain },
  { name: 'speedometer-outline', label: 'Speed', Icon: Gauge },
  { name: 'water-outline', label: 'Water / fuel', Icon: Droplets },
  { name: 'flash-outline', label: 'Charge', Icon: Zap },
  { name: 'moon-outline', label: 'Night', Icon: Moon },
  { name: 'sunny-outline', label: 'Day', Icon: Sun },
  { name: 'snow-outline', label: 'Cold', Icon: Snowflake },
  { name: 'umbrella-outline', label: 'Rain', Icon: Umbrella },
  { name: 'time-outline', label: 'Time', Icon: Clock },
  { name: 'ticket-outline', label: 'Ticket / toll', Icon: Ticket },
  { name: 'gift-outline', label: 'Gift', Icon: Gift },
  { name: 'briefcase-outline', label: 'Luggage', Icon: Briefcase },
  { name: 'construct-outline', label: 'Service', Icon: Wrench },
  { name: 'medkit-outline', label: 'Medical', Icon: HeartPulse },
  { name: 'people-outline', label: 'People', Icon: Users },
  { name: 'call-outline', label: 'Call', Icon: Phone },
  { name: 'star-outline', label: 'Star', Icon: Star },
];

// How a custom charge can be worked out. Plain words first; `needs` says which editors show.
export const CUSTOM_BASES = [
  { value: 'PER_TRIP', label: 'Once per trip', needs: [] },
  { value: 'PER_DAY', label: 'For each day', needs: ['perUnit'] },
  { value: 'PER_NIGHT', label: 'For each night', needs: ['perUnit'] },
  { value: 'PER_HOUR', label: 'For each hour', needs: ['perUnit'] },
  { value: 'PER_KM', label: 'For each km (pickup to drop)', needs: ['perUnit', 'distance'] },
  { value: 'DAY_TABLE', label: 'By number of days (table)', needs: ['perUnit', 'table'] },
  { value: 'KM_BANDS', label: 'By distance (bands)', needs: ['distance', 'bands'] },
];
const basisNeeds = (b) => (CUSTOM_BASES.find((x) => x.value === b) || CUSTOM_BASES[0]).needs;
const UNIT_WORD = { PER_DAY: 'day', PER_NIGHT: 'night', PER_HOUR: 'hour', PER_KM: 'km' };

const defaultTable = () => [{ days: 1, units: 1 }, { days: 2, units: 2 }];
const defaultBands = () => [
  { fromKm: 0, toKm: 100, type: 'FLAT', amount: '' },
  { fromKm: 100, toKm: '', type: 'PER_KM', amount: '' },
];

export const defaultAllowances = () => ({
  dayGraceHours: 4,
  food: {
    enabled: false, label: 'Driver Food', desc: '', amountPerUnit: '', unitName: 'meal',
    schedule: [{ days: 1, units: 2 }, { days: 2, units: 6 }], unitsPerExtraDay: 3, customerCanArrange: true,
  },
  stay: {
    enabled: false, label: 'Driver Stay', desc: '', amountPerUnit: '', unitName: 'night',
    schedule: [{ days: 1, units: 0 }, { days: 2, units: 1 }], unitsPerExtraDay: 1, customerCanArrange: true,
  },
  busFare: {
    enabled: false, label: 'Driver return bus fare', desc: '', roadFactor: 1.3, customerCanArrange: true,
    slabs: [
      { fromKm: 0, toKm: 100, type: 'FLAT', amount: '' },
      { fromKm: 100, toKm: 300, type: 'FLAT', amount: '' },
      { fromKm: 300, toKm: '', type: 'PER_KM', amount: '' },
    ],
  },
  custom: [],
});

const rowsFromSaved = (rows, fallback) => (Array.isArray(rows) ? rows.map((r) => ({ days: r.days, units: r.units })) : fallback);
const bandsFromSaved = (slabs, fallback) => (Array.isArray(slabs)
  ? slabs.map((s) => ({ fromKm: s.fromKm, toKm: s.toKm ?? '', type: s.type || 'FLAT', amount: s.amount ?? '' }))
  : fallback);

/** A saved config's allowances laid over the defaults, as plain form values. */
export const allowancesFromConfig = (saved) => {
  const d = defaultAllowances();
  if (!saved) return d;
  const unit = (def, s) => (s ? {
    ...def, ...s, amountPerUnit: s.amountPerUnit ?? '', schedule: rowsFromSaved(s.schedule, def.schedule),
  } : def);
  return {
    dayGraceHours: saved.dayGraceHours ?? d.dayGraceHours,
    food: unit(d.food, saved.food),
    stay: unit(d.stay, saved.stay),
    busFare: saved.busFare ? { ...d.busFare, ...saved.busFare, slabs: bandsFromSaved(saved.busFare.slabs, d.busFare.slabs) } : d.busFare,
    custom: Array.isArray(saved.custom)
      ? saved.custom.map((c) => ({
        ...c,
        basis: c.basis || 'PER_TRIP',
        amount: c.amount ?? '',
        unitName: c.unitName || '',
        freeUnits: c.freeUnits ?? 0,
        minAmount: c.minAmount ?? 0,
        maxAmount: c.maxAmount ?? 0,
        unitsPerExtraDay: c.unitsPerExtraDay ?? 0,
        roadFactor: c.roadFactor ?? 1.3,
        schedule: rowsFromSaved(c.schedule, defaultTable()),
        slabs: bandsFromSaved(c.slabs, defaultBands()),
      }))
      : [],
  };
};

const num = (v) => (v === '' || v == null ? 0 : Number(v));
const cleanRows = (rows) => (rows || []).filter((r) => r.days !== '' && r.units !== '').map((r) => ({ days: Number(r.days), units: Number(r.units) }));
const cleanBands = (slabs) => (slabs || []).filter((s) => s.fromKm !== '' && s.amount !== '').map((s) => ({
  fromKm: Number(s.fromKm),
  toKm: s.toKm === '' || s.toKm == null ? null : Number(s.toKm),
  type: s.type || 'FLAT',
  amount: Number(s.amount),
}));

/** The form's allowances as the API stores them. */
export const allowancesToPayload = (a) => {
  const unit = (u) => ({
    enabled: !!u.enabled,
    label: u.label || '',
    desc: u.desc || '',
    icon: u.icon || '',
    amountPerUnit: num(u.amountPerUnit),
    unitName: u.unitName || '',
    schedule: cleanRows(u.schedule),
    unitsPerExtraDay: num(u.unitsPerExtraDay),
    customerCanArrange: u.customerCanArrange !== false,
  });
  return {
    dayGraceHours: num(a.dayGraceHours),
    food: unit(a.food),
    stay: unit(a.stay),
    busFare: {
      enabled: !!a.busFare.enabled,
      label: a.busFare.label || '',
      desc: a.busFare.desc || '',
      icon: a.busFare.icon || '',
      roadFactor: num(a.busFare.roadFactor) || 1.3,
      customerCanArrange: a.busFare.customerCanArrange !== false,
      slabs: cleanBands(a.busFare.slabs),
    },
    custom: (a.custom || []).filter((c) => String(c.label || '').trim()).map((c) => {
      const needs = basisNeeds(c.basis);
      return {
        key: c.key,
        label: c.label,
        desc: c.desc || '',
        icon: c.icon || '',
        enabled: c.enabled !== false,
        appliesTo: c.appliesTo || 'ALL',
        basis: c.basis || 'PER_TRIP',
        amount: needs.includes('bands') ? 0 : num(c.amount),
        unitName: needs.includes('perUnit') ? (c.unitName || '') : '',
        freeUnits: needs.includes('perUnit') ? num(c.freeUnits) : 0,
        minAmount: num(c.minAmount),
        maxAmount: num(c.maxAmount),
        ...(needs.includes('table') ? { schedule: cleanRows(c.schedule), unitsPerExtraDay: num(c.unitsPerExtraDay) } : {}),
        ...(needs.includes('distance') ? { roadFactor: num(c.roadFactor) || 1.3 } : {}),
        ...(needs.includes('bands') ? { slabs: cleanBands(c.slabs) } : {}),
        customerCanArrange: c.customerCanArrange !== false,
      };
    }),
  };
};

// Units earned by a trip of `days` days: the row with the most days not above it, plus
// unitsPerExtraDay for every day beyond that row. Mirrors the engine.
const unitsFor = (u, days) => {
  const rows = (u.schedule || [])
    .map((r) => ({ days: Number(r.days), units: Number(r.units) }))
    .filter((r) => r.days >= 1 && Number.isFinite(r.units))
    .sort((x, y) => x.days - y.days);
  const per = Number(u.unitsPerExtraDay) || 0;
  const base = [...rows].reverse().find((r) => r.days <= days);
  if (!base) return rows.length ? rows[0].units : days * per;
  return base.units + (days - base.days) * per;
};

// A band's price for a distance. Mirrors the engine.
const bandPrice = (slabsIn, km) => {
  const slabs = (slabsIn || [])
    .map((s) => ({ from: Number(s.fromKm), to: s.toKm === '' || s.toKm == null ? null : Number(s.toKm), type: s.type, amount: Number(s.amount) || 0 }))
    .sort((a, b) => a.from - b.from);
  const slab = slabs.find((s) => km >= s.from && (s.to === null || km <= s.to));
  if (!slab) return null;
  return slab.type === 'PER_KM' ? Math.round(slab.amount * km) : Math.round(slab.amount);
};

// What one custom charge comes to for a trip of `days` days (null = depends on distance).
const customPreview = (c, days) => {
  const needs = basisNeeds(c.basis);
  if (needs.includes('distance')) return null;
  const amount = Number(c.amount) || 0;
  let units = 1;
  if (c.basis === 'PER_DAY') units = days;
  else if (c.basis === 'PER_NIGHT') units = Math.max(0, days - 1);
  else if (c.basis === 'PER_HOUR') return null;
  else if (c.basis === 'DAY_TABLE') units = unitsFor(c, days);
  const charged = Math.max(0, units - (Number(c.freeUnits) || 0));
  let total = Math.round(charged * amount);
  if (charged > 0) {
    if (Number(c.minAmount) > 0) total = Math.max(total, Number(c.minAmount));
    if (Number(c.maxAmount) > 0) total = Math.min(total, Number(c.maxAmount));
  }
  return total;
};

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const Field = ({ label, children, hint }) => (
  <div>
    <label className="form-label">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const Switch = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

// Pick the icon the customer sees on the booking screen, from a dropdown. "Default" leaves it
// to the app (a sensible icon for Stay / Food / bus fare, a cash icon for custom charges).
// A native <select> cannot show an icon beside each option, so this is a small custom one:
// click to open, click an icon to choose, click outside or press Escape to close.
const IconPicker = ({ value, onChange, defaultIcon }) => {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const chosen = ICON_CHOICES.find((i) => i.name === value);
  const fallback = ICON_CHOICES.find((i) => i.name === defaultIcon);
  const Shown = (chosen || fallback || {}).Icon;
  const pick = (name) => { onChange(name); setOpen(false); };

  return (
    <div ref={box} className="relative max-w-xs">
      <label className="form-label">Icon on the booking screen</label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="input w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="flex items-center gap-2 min-w-0">
          {Shown ? <Shown className="h-4 w-4 flex-shrink-0 text-gray-700" /> : <Ban className="h-4 w-4 flex-shrink-0 text-gray-400" />}
          <span className="truncate">{chosen ? chosen.label : 'Default'}</span>
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          <li
            role="option"
            aria-selected={!value}
            onClick={() => pick('')}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 ${!value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
          >
            {fallback ? <fallback.Icon className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
            Default
          </li>
          {ICON_CHOICES.map(({ name, label, Icon }) => (
            <li
              key={name}
              role="option"
              aria-selected={value === name}
              onClick={() => pick(name)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 ${value === name ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// "A trip of … days earns … units" rows.
const TableEditor = ({ rows, onChange, unitWord }) => {
  const setRow = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => {
    const last = rows[rows.length - 1];
    onChange([...rows, { days: last ? Number(last.days) + 1 : 1, units: '' }]);
  };
  return (
    <div>
      <p className="text-xs font-medium text-gray-600 mb-1">A trip of … days earns … {unitWord}s</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-8">Days</span>
            <input type="number" min="1" className="input w-24" value={r.days} onChange={(e) => setRow(i, { days: e.target.value })} />
            <span className="text-xs text-gray-500">earns</span>
            <input type="number" min="0" className="input w-24" value={r.units} onChange={(e) => setRow(i, { units: e.target.value })} />
            <span className="text-xs text-gray-500">{unitWord}s</span>
            <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1 text-red-500 hover:text-red-700" title="Remove row"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" onClick={addRow} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
          <Plus className="h-3 w-3" /> Add a row
        </button>
      </div>
    </div>
  );
};

// Distance bands: flat amount or per km, no gaps, no overlaps.
const BandsEditor = ({ slabs, onChange }) => {
  const setSlab = (i, patch) => onChange(slabs.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addSlab = () => {
    const last = slabs[slabs.length - 1];
    onChange([...slabs, { fromKm: last && last.toKm !== '' ? last.toKm : '', toKm: '', type: 'FLAT', amount: '' }]);
  };
  return (
    <div>
      <p className="text-xs font-medium text-gray-600 mb-1">Distance bands (km)</p>
      <div className="space-y-2">
        {slabs.map((s, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500">From</span>
            <input type="number" min="0" className="input w-24" value={s.fromKm} onChange={(e) => setSlab(i, { fromKm: e.target.value })} />
            <span className="text-xs text-gray-500">to</span>
            <input type="number" min="0" className="input w-24" value={s.toKm} placeholder="no limit" onChange={(e) => setSlab(i, { toKm: e.target.value })} />
            <span className="text-xs text-gray-500">km</span>
            <select className="input w-32" value={s.type} onChange={(e) => setSlab(i, { type: e.target.value })}>
              <option value="FLAT">Flat amount</option>
              <option value="PER_KM">Per km</option>
            </select>
            <input type="number" min="0" className="input w-28" value={s.amount} placeholder="₹" onChange={(e) => setSlab(i, { amount: e.target.value })} />
            <button type="button" onClick={() => onChange(slabs.filter((_, j) => j !== i))} className="p-1 text-red-500 hover:text-red-700" title="Remove band"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" onClick={addSlab} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
          <Plus className="h-3 w-3" /> Add a band
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-1">Bands must start at 0 and run on without gaps or overlaps. Leave &ldquo;to&rdquo; empty on the last band for no upper limit.</p>
    </div>
  );
};

const BandPreview = ({ slabs, roadFactorHint }) => (
  <div className="rounded-md bg-gray-50 p-3">
    <p className="text-xs font-medium text-gray-600 mb-2">Sample road distances{roadFactorHint ? ` (${roadFactorHint})` : ''}</p>
    <div className="grid grid-cols-4 gap-2 text-xs text-center">
      {[50, 150, 250, 400].map((km) => {
        const p = bandPrice(slabs, km);
        return (
          <div key={km} className="rounded bg-white border border-gray-100 py-1">
            <div className="text-gray-400">{km} km</div>
            <div className="font-semibold text-gray-900">{p === null ? 'no band' : inr(p)}</div>
          </div>
        );
      })}
    </div>
  </div>
);

// Food and Stay share one editor.
const UnitEditor = ({ title, value, onChange, unitWord, intro }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const amount = Number(value.amountPerUnit) || 0;

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        <Switch checked={value.enabled} onChange={(v) => set({ enabled: v })} label="Charge this" />
      </div>
      <p className="text-xs text-gray-500">{intro}</p>

      {value.enabled && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Name shown to the customer">
              <input className="input" value={value.label} onChange={(e) => set({ label: e.target.value })} />
            </Field>
            <Field label={`Amount for one ${unitWord} (₹)`}>
              <input type="number" min="0" className="input" value={value.amountPerUnit} onChange={(e) => set({ amountPerUnit: e.target.value })} />
            </Field>
            <Field label={`Extra ${unitWord}s for each day beyond the table`}>
              <input type="number" min="0" className="input" value={value.unitsPerExtraDay} onChange={(e) => set({ unitsPerExtraDay: e.target.value })} />
            </Field>
          </div>

          <TableEditor rows={value.schedule} onChange={(schedule) => set({ schedule })} unitWord={unitWord} />

          <IconPicker value={value.icon} onChange={(icon) => set({ icon })} defaultIcon={unitWord === 'meal' ? 'restaurant-outline' : 'bed-outline'} />

          <div className="rounded-md bg-gray-50 p-3">
            <p className="text-xs font-medium text-gray-600 mb-2">What the customer would be charged if they do not arrange it</p>
            <div className="grid grid-cols-5 gap-2 text-xs text-center">
              {[1, 2, 3, 4, 5].map((d) => {
                const u = unitsFor(value, d);
                return (
                  <div key={d} className="rounded bg-white border border-gray-100 py-1">
                    <div className="text-gray-400">{d} day{d > 1 ? 's' : ''}</div>
                    <div className="text-gray-700">{u} {unitWord}{u === 1 ? '' : 's'}</div>
                    <div className="font-semibold text-gray-900">{inr(u * amount)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <Switch
            checked={value.customerCanArrange !== false}
            onChange={(v) => set({ customerCanArrange: v })}
            label="The customer may choose to arrange this themselves (untick to always charge it)"
          />
        </>
      )}
    </div>
  );
};

// One custom charge: pick how it is charged, and only the fields that apply appear.
const CustomEditor = ({ value: c, onChange, onRemove, onDuplicate }) => {
  const set = (patch) => onChange({ ...c, ...patch });
  const needs = basisNeeds(c.basis);
  const unitWord = c.unitName || UNIT_WORD[c.basis] || 'unit';

  // Switching how it is charged keeps what was typed and seeds the editors it newly needs.
  const setBasis = (basis) => {
    const n = basisNeeds(basis);
    set({
      basis,
      schedule: n.includes('table') && !(c.schedule || []).length ? defaultTable() : c.schedule,
      slabs: n.includes('bands') && !(c.slabs || []).length ? defaultBands() : c.slabs,
    });
  };

  const showPreview = !needs.includes('distance') && c.basis !== 'PER_HOUR' && Number(c.amount) > 0;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <Field label="Name shown to the customer">
          <input className="input" value={c.label} onChange={(e) => set({ label: e.target.value })} />
        </Field>
        <Field label="How is it charged?">
          <select className="input" value={c.basis} onChange={(e) => setBasis(e.target.value)}>
            {CUSTOM_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </Field>
        {!needs.includes('bands') && (
          <Field label={needs.includes('perUnit') ? `Amount for one ${unitWord} (₹)` : 'Amount (₹)'}>
            <input type="number" min="0" className="input" value={c.amount} onChange={(e) => set({ amount: e.target.value })} />
          </Field>
        )}
        <Field label="Applies to">
          <select className="input" value={c.appliesTo} onChange={(e) => set({ appliesTo: e.target.value })}>
            <option value="ALL">All trips</option>
            <option value="ONE_WAY">One-way only</option>
            <option value="ROUND_TRIP">Round trip only</option>
          </select>
        </Field>
      </div>

      {needs.includes('table') && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Field label="What is one unit called?" hint="For example snack, bottle, pass.">
              <input className="input" value={c.unitName} placeholder="unit" onChange={(e) => set({ unitName: e.target.value })} />
            </Field>
            <Field label={`Extra ${unitWord}s for each day beyond the table`}>
              <input type="number" min="0" className="input" value={c.unitsPerExtraDay} onChange={(e) => set({ unitsPerExtraDay: e.target.value })} />
            </Field>
          </div>
          <TableEditor rows={c.schedule || []} onChange={(schedule) => set({ schedule })} unitWord={unitWord} />
        </>
      )}

      <IconPicker value={c.icon} onChange={(icon) => set({ icon })} defaultIcon="cash-outline" />

      {needs.includes('bands') && <BandsEditor slabs={c.slabs || []} onChange={(slabs) => set({ slabs })} />}
      {needs.includes('bands') && <BandPreview slabs={c.slabs} roadFactorHint={`road factor ${c.roadFactor || 1.3}`} />}

      {needs.includes('distance') && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Field label="Road factor" hint="Straight-line pickup to drop distance × this ≈ road distance.">
            <input type="number" step="0.05" min="1" className="input" value={c.roadFactor} onChange={(e) => set({ roadFactor: e.target.value })} />
          </Field>
        </div>
      )}

      {needs.includes('perUnit') && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Field label={`First … ${unitWord}s are free`} hint="0 = charge from the first one.">
            <input type="number" min="0" className="input" value={c.freeUnits} onChange={(e) => set({ freeUnits: e.target.value })} />
          </Field>
          <Field label="Least this line can be (₹)" hint="0 = no minimum.">
            <input type="number" min="0" className="input" value={c.minAmount} onChange={(e) => set({ minAmount: e.target.value })} />
          </Field>
          <Field label="Most this line can be (₹)" hint="0 = no limit.">
            <input type="number" min="0" className="input" value={c.maxAmount} onChange={(e) => set({ maxAmount: e.target.value })} />
          </Field>
        </div>
      )}

      {showPreview && (
        <div className="rounded-md bg-white border border-gray-100 p-3">
          <p className="text-xs font-medium text-gray-600 mb-2">If the customer does not arrange it</p>
          <div className="grid grid-cols-5 gap-2 text-xs text-center">
            {[1, 2, 3, 4, 5].map((d) => (
              <div key={d} className="rounded bg-gray-50 py-1">
                <div className="text-gray-400">{d} day{d > 1 ? 's' : ''}</div>
                <div className="font-semibold text-gray-900">{inr(customPreview(c, d))}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {c.basis === 'PER_HOUR' && <p className="text-xs text-gray-500">Charged for the booked hours at booking, and for the real hours (rounded up to the half hour, never fewer than booked) at the end of the trip.</p>}

      <div className="flex flex-wrap items-center gap-4">
        <Switch checked={c.enabled !== false} onChange={(v) => set({ enabled: v })} label="On" />
        <Switch checked={c.customerCanArrange !== false} onChange={(v) => set({ customerCanArrange: v })} label="Customer may arrange it themselves" />
        <button type="button" onClick={onDuplicate} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
          <Copy className="h-3 w-3" /> Duplicate
        </button>
        <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700">
          <Trash2 className="h-3 w-3" /> Remove
        </button>
      </div>
    </div>
  );
};

const newKey = () => `custom_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

const AllowancesSection = ({ value, onChange }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const bus = value.busFare;
  const setBus = (patch) => set({ busFare: { ...bus, ...patch } });

  const addCustom = () => set({
    custom: [...value.custom, {
      key: newKey(), label: '', desc: '', icon: '', enabled: true, appliesTo: 'ALL', basis: 'PER_TRIP', amount: '',
      unitName: '', freeUnits: 0, minAmount: 0, maxAmount: 0, unitsPerExtraDay: 0, roadFactor: 1.3,
      schedule: defaultTable(), slabs: defaultBands(), customerCanArrange: true,
    }],
  });
  const setCustom = (i, next) => set({ custom: value.custom.map((c, j) => (j === i ? next : c)) });
  const duplicateCustom = (i) => set({
    custom: [...value.custom.slice(0, i + 1), { ...value.custom[i], key: newKey(), label: `${value.custom[i].label} (copy)` }, ...value.custom.slice(i + 1)],
  });

  return (
    <div className="card p-6 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800">Driver allowances (Food, Stay, Bus fare, custom)</h3>
        <p className="text-xs text-gray-500 mt-1">
          On the booking screen the customer ticks an option to say &ldquo;I will arrange this myself&rdquo; — nothing is charged for it.
          An option they leave unticked is added to the fare as its own line, and is worked out again at the end of the trip from the
          real number of days, hours and the real drop point (a 2-day booking that runs 4 days pays for 4). Everything stays off until you switch it on.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field
          label="Extra day starts after (hours)"
          hint="A trip counts as another day only once it passes this many hours beyond the previous 24-hour span, so a short run over midnight is not an extra day. Never fewer days than booked."
        >
          <input type="number" min="0" max="23" className="input" value={value.dayGraceHours} onChange={(e) => set({ dayGraceHours: e.target.value })} />
        </Field>
      </div>

      <UnitEditor
        title="Food"
        unitWord="meal"
        intro="Example: 1 day earns 2 meals, 2 days earn 6, and every extra day adds 3."
        value={value.food}
        onChange={(food) => set({ food })}
      />
      <UnitEditor
        title="Stay"
        unitWord="night"
        intro="Example: 1 day earns 0 nights, 2 days earn 1, and every extra day adds 1."
        value={value.stay}
        onChange={(stay) => set({ stay })}
      />

      <div className="rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800">One-way return bus fare</h4>
          <Switch checked={bus.enabled} onChange={(v) => setBus({ enabled: v })} label="Charge this" />
        </div>
        <p className="text-xs text-gray-500">
          One-way trips only. The driver travels back from the drop to the pickup; the fare comes from how far that is, using the bands below.
        </p>
        {bus.enabled && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Name shown to the customer">
                <input className="input" value={bus.label} onChange={(e) => setBus({ label: e.target.value })} />
              </Field>
              <Field label="Road factor" hint="Straight-line distance × this ≈ road distance. 1.3 is typical.">
                <input type="number" step="0.05" min="1" className="input" value={bus.roadFactor} onChange={(e) => setBus({ roadFactor: e.target.value })} />
              </Field>
            </div>
            <BandsEditor slabs={bus.slabs} onChange={(slabs) => setBus({ slabs })} />
            <BandPreview slabs={bus.slabs} />
            <IconPicker value={bus.icon} onChange={(icon) => setBus({ icon })} defaultIcon="bus-outline" />
            <Switch
              checked={bus.customerCanArrange !== false}
              onChange={(v) => setBus({ customerCanArrange: v })}
              label="The customer may choose to arrange this themselves (untick to always charge it)"
            />
          </>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800">Custom charges ({value.custom.length})</h4>
          <button type="button" onClick={addCustom} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
            <Plus className="h-3 w-3" /> Add a charge
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Add as many as you need. Each one can be charged once per trip, for each day, night, hour or km, by a table of days,
          or by distance bands — with free units and a minimum or maximum if you want them.
        </p>
        {value.custom.length === 0 && <p className="text-xs text-gray-400">None yet.</p>}
        {value.custom.map((c, i) => (
          <CustomEditor
            key={c.key}
            value={c}
            onChange={(next) => setCustom(i, next)}
            onRemove={() => set({ custom: value.custom.filter((_, j) => j !== i) })}
            onDuplicate={() => duplicateCustom(i)}
          />
        ))}
        {value.custom.length > 0 && (
          <button type="button" onClick={addCustom} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
            <Plus className="h-3 w-3" /> Add another charge
          </button>
        )}
      </div>
    </div>
  );
};

export default AllowancesSection;
